import { formatISO, startOfMonth, startOfWeek } from 'date-fns';
import { css, html } from 'react-strict-dom';

import { Api } from '../../../api';
import { Dropdown, Text } from '../../../components';
import { Model } from '../../../models';
import { formatTimeSpent } from '../../../util';
import WorkDoneDropdown from './WorkDoneDropdown';

type Props = Readonly<{
  company: Omit<Api.Company.V_AdminList, 'slaInitialReply'>;
}>;

function WorkDoneLogger({
  company,
  workDones,
  category,
  invalidate,
}: {
  company: Omit<Api.Company.V_AdminList, 'slaInitialReply'>;
  workDones: Api.WorkDone.V_List[] | null | undefined;
  category: string;
  invalidate: () => unknown;
}) {
  const timeSpent = Model.WorkDone.sumMinutes(workDones);
  return (
    <html.div style={styles.workDoneCategory}>
      <Text style={styles.workDoneLabel}>
        {Model.WorkDoneCategory.format(category)}
      </Text>
      <Dropdown
        anchorSide="left"
        trigger={
          <html.div style={styles.workDoneButton}>
            <i className="fa-solid fa-stopwatch" style={{ marginRight: 4 }} />
            <Text style={styles.workDoneTime}>
              {formatTimeSpent(timeSpent)}
            </Text>
          </html.div>
        }
      >
        <WorkDoneDropdown
          defaultValues={{
            company: `/companies/${company.id}`,
            categoryString: category,
          }}
          workDones={Model.WorkDone.filterLogged(workDones ?? [])}
          invalidate={invalidate}
        />
      </Dropdown>
    </html.div>
  );
}

export default function CompanyAgencyControls({ company }: Props) {
  const cutoffDate = company.maintenanceHoursWeekly
    ? startOfWeek(new Date(), { weekStartsOn: 1 })
    : startOfMonth(new Date());
  const apiWorkDones = Api.WorkDone.useReadCollection({
    company: company['@id'],
    'loggedAt[after]': formatISO(cutoffDate, {
      representation: 'date',
    }),
  });

  if (apiWorkDones.error) throw apiWorkDones.error;

  return (
    <html.div style={styles.container}>
      <html.div style={styles.list}>
        {Model.WorkDoneCategory.ALL.map((category) => (
          <WorkDoneLogger
            key={category}
            company={company}
            workDones={apiWorkDones.data?.['hydra:member']?.filter(
              (wd: Api.WorkDone.V_List) => wd.categoryString === category
            )}
            category={category}
            invalidate={apiWorkDones.invalidate}
          />
        ))}
      </html.div>
      <Text as="p" style={styles.detail}>
        Time unrelated to any specific ticket (current{' '}
        {company.maintenanceHoursWeekly ? 'week' : 'month'})
      </Text>
    </html.div>
  );
}

const styles = css.create({
  container: {
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ddd',
    borderRadius: 6,
    paddingBlock: 8,
    paddingInline: 16,
  },
  list: {
    display: 'flex',
    gap: 15,
  },
  detail: {
    marginTop: 5,
    fontSize: 12,
    color: '#7f858b',
  },
  workDoneCategory: {
    display: 'flex',
    alignItems: 'center',
  },
  workDoneLabel: {
    fontSize: 14,
  },
  workDoneTime: {
    fontSize: 12,
  },
  workDoneButton: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    paddingBlock: 2,
    paddingInline: 4,
    marginLeft: 4,
    borderRadius: 3,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: '#cfd6dd',
      color: '#0366d6',
    },
  },
});
